import React, { useCallback, useEffect, useState } from 'react'
import {
	Box,
	Link,
	Typography,
} from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { MY_BUSINESS_ROUTES } from '@constants/routes'
import { AppPillToggle, AppResultsSkeleton, AppNote } from '@components'
import { dsColorNew } from '@palette'
import { MbPage, MbKpiCard, MbKpiRow, MbPageSkeleton, mbSectionBarSx } from 'Pages/MyBusiness/_shared/Ui'

import {
	FiltersBar,
	defaultFilters,
	filtersToQuery,
} from 'Pages/MyBusiness/_shared/Filters'
import { PerformanceMatrix, PartnerDrillDialog } from 'Pages/MyBusiness/_shared/Charts'

/**
 * Performance — Channel × Category heatmap matrix.
 *
 * Backend: GET /api/my-business/radar with the same filter contract as
 * /revenue. Click on any cell opens the Partner drill dialog scoped to
 * the (channel, sipp) pair.
 */
const METRICS = [
	{ key: 'revenue',  label: 'Revenue' },
	{ key: 'bookings', label: 'Bookings' },
	{ key: 'days',     label: 'Days' },
	{ key: 'rpd',      label: 'RPD' },
]

const fmtEur = (n) =>
	new Intl.NumberFormat('es-ES', {
		style: 'currency', currency: 'EUR',
		minimumFractionDigits: 0, maximumFractionDigits: 0,
	}).format(n || 0)
const fmtNum = (n) => new Intl.NumberFormat('es-ES').format(n || 0)
const fmtPct = (n) => (n == null ? '—' : `${n > 0 ? '+' : ''}${n.toFixed(1)}%`)

export default function MyBusinessPerformancePage() {
	const [applied, setApplied] = useState(defaultFilters)
	const [draft,   setDraft]   = useState(defaultFilters)
	const [data,    setData]    = useState(null)
	const [error,   setError]   = useState(null)
	// Switching Group by only reloads the matrix
	const [matrixReloading, setMatrixReloading] = useState(false)
	const [loading, setLoading] = useState(true)
	const [metric,  setMetric]  = useState('revenue')
	const [groupBy, setGroupBy] = useState('channel_type')
	const [drillAnchor, setDrillAnchor] = useState(null)

	const fetchPerformance = useCallback((f, gb) => {
		setLoading(true); setError(null)
		fetch(`/api/my-business/radar?${filtersToQuery(f)}&group_by=${gb}`,
		      { credentials: 'include' })
			.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
			.then(setData)
			.catch(e => setError(e.message))
			.finally(() => { setLoading(false); setMatrixReloading(false) })
	}, [])

	const handleApply = useCallback((next) => {
		setApplied(next); setDraft(next); fetchPerformance(next, groupBy)
	}, [fetchPerformance, groupBy])

	const handleGroupBy = (gb) => {
		setMatrixReloading(true)
		setGroupBy(gb)
		fetchPerformance(applied, gb)
	}

	const pageLoading = loading && !matrixReloading

	useEffect(() => { fetchPerformance(applied, groupBy) }, [])  // initial

	const totals = data?.totals
	const totalCards = [
		{ key: 'revenue',  label: 'Revenue',  format: fmtEur, icon: 'sack-dollar' },
		{ key: 'bookings', label: 'Bookings', format: fmtNum, icon: 'calendar-check' },
		{ key: 'days',     label: 'Days',     format: fmtNum, icon: 'calendar-days' },
		{ key: 'rpd',      label: 'RPD',      format: fmtEur, icon: 'euro-sign' },
	]

	return (
		<MbPage title={MY_BUSINESS_ROUTES.PERFORMANCE_TITLE}
		        subtitle={MY_BUSINESS_ROUTES.PERFORMANCE_SUBTITLE}>
			<Box>
				<FiltersBar value={draft} applied={applied}
				            onChange={setDraft} onApply={handleApply}
				            loading={loading}
				            csvHref={`/api/my-business/revenue/download?${filtersToQuery(applied)}`}
				            csvLabel="Bookings CSV"/>


				{error && <AppNote severity="error" sx={{ mb: 2 }}>Backend unreachable: {error}</AppNote>}

				{pageLoading && <MbPageSkeleton kpis={4} blocks={['table']}/>}

				{data && !pageLoading && (
					<Box>
						{/* Totals row */}
						<MbKpiRow>
							{totalCards.map((c) => {
								const yoy = totals?.[`${c.key}_yoy`]
								return (
									<MbKpiCard key={c.key}
									           icon={c.icon}
									           label={c.label}
									           value={c.format(totals?.[c.key])}
									           delta={yoy ?? undefined}
									           deltaLabel={yoy != null ? fmtPct(yoy) : undefined}/>
								)
							})}
						</MbKpiRow>

						{/* Matrix controls */}
						<Box justifyContent="flex-end" sx={{ mt: 3, mb: 2, ...mbSectionBarSx }}>
							<Box display="flex" alignItems="center" gap={1.5}>
								<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
									Group by
								</Typography>
								<AppPillToggle size="small" value={groupBy} onChange={handleGroupBy} options={[
									{ value: 'channel_type', label: 'Type' },
									{ value: 'channel_name', label: 'Name' },
								]}/>
							</Box>
							<Box display="flex" alignItems="center" gap={1.5}>
								<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, ml: 1 }}>
									Metric
								</Typography>
								<AppPillToggle size="small" value={metric} onChange={setMetric}
								               options={METRICS.map(m => ({ value: m.key, label: m.label }))}/>
							</Box>
						</Box>

						{groupBy === 'channel_type' && (data.distinct_groups ?? 0) <= 1 && (
							<AppNote severity="warning" sx={{ mb: 2 }}>
								<Box component="span" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>Matrix collapsed to a single column</Box>
								Only one <strong>Channel Type</strong> resolved in this period.
								Map your booking channels to Direct / B2B / OTA / External / Other
								in{' '}
								<Link component={RouterLink}
								      to={`${MY_BUSINESS_ROUTES.ADMIN_PATH}?tab=channels`}>
									Admin → Channels
								</Link>{' '}
								so the radar can spread across multiple columns. Meanwhile you can
								switch <strong>Group by</strong> to <strong>Name</strong> to see
								the raw PMS channels.
							</AppNote>
						)}

						{matrixReloading ? <AppResultsSkeleton chart height={520}/> : <PerformanceMatrix columns={data.columns}
						                   metric={metric}
						                   topN={15}
						                   onCellClick={groupBy === 'channel_type'
							                   ? (channelType, sippCode) =>
								                     setDrillAnchor({ channelType, sipp: sippCode })
							                   : undefined}/>}

						<PartnerDrillDialog anchor={drillAnchor}
						                    filters={applied}
						                    onClose={() => setDrillAnchor(null)}/>
					</Box>
				)}
			</Box>
		</MbPage>
	)
}
